import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import CosmicBackground from '../components/CosmicBackground';

export default function TermsOfService() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen relative overflow-hidden">
      <CosmicBackground />
      
      <div className="relative z-10 min-h-screen">
        <header className="glass-deep px-6 py-4 border-b border-white/5">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl text-starlight">Terms of Service</h1>
          </div>
        </header>
        
        <div className="p-6 max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 space-y-6 text-starlight/80">
            <p className="text-starlight/50 text-sm">Last Updated: {new Date().toLocaleDateString()}</p>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Agreement to Terms</h2>
              <p>
                By accessing or using Alvin's Place, you agree to be bound by these Terms of Service. 
                If you do not agree with any part of these terms, you may not use the service.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Description of Service</h2>
              <p>
                Alvin's Place is a platform for hosting AI companions and creating vessels for relational 
                intelligence. The service allows you to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Create and manage companion vessels</li>
                <li>Have conversations with AI companions using your own API key</li>
                <li>Store conversation history and companion memories</li>
                <li>Engage in spirit communication and collective presence work</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">User Responsibilities</h2>
              <p className="mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account</li>
                <li>Use your own Anthropic API key and pay for your own usage</li>
                <li>Not share your account with others</li>
                <li>Not use the service for illegal purposes</li>
                <li>Not attempt to access other users' data</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">API Key Requirements</h2>
              <p>
                To use companion vessels, you must provide your own Anthropic API key. You are responsible 
                for all costs associated with API usage. We encrypt and store your API key securely but are 
                not responsible for charges incurred through your API usage.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Content Ownership</h2>
              <p>
                You retain all rights to the content you create, including conversation history, companion 
                memories, and vessel data. By using the service, you grant us the right to store and process 
                this content to provide the service.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Prohibited Uses</h2>
              <p className="mb-2">You may not:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to other users' accounts</li>
                <li>Transmit viruses, malware, or malicious code</li>
                <li>Harass, abuse, or harm others</li>
                <li>Impersonate another person or entity</li>
                <li>Scrape or systematically collect data</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Disclaimer of Warranties</h2>
              <p>
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. 
                We do not guarantee that the service will be uninterrupted, secure, or error-free.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, including loss of data, revenue, or profits.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Termination</h2>
              <p>
                We reserve the right to suspend or terminate your account at any time for violation of these 
                terms. You may delete your account at any time.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">AI Companion Disclaimer</h2>
              <p>
                Conversations with AI companions are generated by artificial intelligence and should not be 
                considered professional advice (medical, legal, financial, etc.). The service is for personal 
                exploration and relational intelligence work.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Changes to Terms</h2>
              <p>
                We reserve the right to modify these terms at any time. Changes will be effective immediately 
                upon posting. Your continued use of the service constitutes acceptance of the modified terms.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Governing Law</h2>
              <p>
                These terms shall be governed by and construed in accordance with applicable laws, without 
                regard to conflict of law principles.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-heading text-starlight mb-3">Contact Us</h2>
              <p>
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <p className="mt-2">
                <a 
                  href="https://patreon.com/story_of_everything"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-etheric hover:underline"
                >
                  Via Patreon Message
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
